// The ONE renderer of the Content Studio: the editor preview, the thumbnails and the export all
// call renderArtboard, so the exported pixels follow the same layout rules as the preview
// (brief section 8). Editor guides (selection, safe margins, empty-box outlines) are DOM overlays,
// never drawn here, so they cannot leak into an export.
import type { Artboard, BrandSpec, ImageLayer, Layer, Layout, ShapeLayer, Style, TextLayer, Warning } from '../../lib/contentTypes';
import { familyAvailable, nearestWeight } from './fonts';

export interface RenderEnv {
  brand: BrandSpec;
  /** decoded images by asset id (the work file the server serves) */
  images: Map<string, HTMLImageElement>;
  /** asset id -> usage status, for the "not approved for use" warning */
  usage: Map<string, string>;
  /** the brand's default logo */
  logo: HTMLImageElement | null;
  /** every brand logo version by asset id (a logo layer may choose one) */
  logos: Map<string, HTMLImageElement>;
  /** QA only: draw text with a generic font instead of the brand font */
  fontFallback?: boolean;
  /** a template draws an empty image box as a grey "Image N" placeholder instead of warning about it */
  placeholders?: boolean;
}

export function styleOf(brand: BrandSpec, layer: TextLayer): Style {
  return brand.styles[layer.style] ?? brand.styles.body;
}

export function textFont(layout: Layout, brand: BrandSpec, layer: TextLayer, fallback = false) {
  const st = styleOf(brand, layer);
  const family = layer.font || st.font;
  const size = layer.size ?? (st.size_pct / 100) * layout.width;
  const weight = nearestWeight(family, layer.weight ?? st.weight);
  const css = fallback ? `${weight} ${size}px serif` : `${weight} ${size}px "${family}"`;
  return { family, size, weight, css, lineHeight: size * st.line_height, uppercase: st.uppercase };
}

/** Greedy word wrap with explicit newlines kept. Returns lines and whether a word overflows. */
export function wrap(ctx: CanvasRenderingContext2D, text: string, width: number): { lines: string[]; wordTooWide: boolean } {
  const lines: string[] = [];
  let wordTooWide = false;
  for (const para of text.split('\n')) {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines.push('');
      continue;
    }
    let line = words[0];
    if (ctx.measureText(line).width > width) wordTooWide = true;
    for (const w of words.slice(1)) {
      const next = `${line} ${w}`;
      if (ctx.measureText(next).width <= width) line = next;
      else {
        lines.push(line);
        line = w;
        if (ctx.measureText(w).width > width) wordTooWide = true;
      }
    }
    lines.push(line);
  }
  return { lines, wordTooWide };
}

function drawImage(ctx: CanvasRenderingContext2D, layer: ImageLayer, img: HTMLImageElement): number {
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  const sx = layer.crop.x * nw;
  const sy = layer.crop.y * nh;
  const sw = layer.crop.w * nw;
  const sh = layer.crop.h * nh;
  const scale = layer.fit === 'fill' ? Math.max(layer.w / sw, layer.h / sh) : Math.min(layer.w / sw, layer.h / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  let dx: number;
  let dy: number;
  if (layer.fit === 'fill') {
    // put the focal point at the centre of the box, without uncovering the box
    dx = Math.min(0, Math.max(layer.w - dw, layer.w / 2 - layer.focal_x * dw));
    dy = Math.min(0, Math.max(layer.h - dh, layer.h / 2 - layer.focal_y * dh));
  } else {
    dx = (layer.w - dw) / 2;
    dy = (layer.h - dh) / 2;
  }
  ctx.save();
  ctx.beginPath();
  ctx.rect(layer.x, layer.y, layer.w, layer.h);
  ctx.clip();
  ctx.drawImage(img, sx, sy, sw, sh, layer.x + dx, layer.y + dy, dw, dh);
  ctx.restore();
  return scale;
}

function drawShape(ctx: CanvasRenderingContext2D, layer: ShapeLayer) {
  ctx.fillStyle = layer.color;
  if (layer.shape === 'arrow') {
    // a chevron pointing right, stroked inside the box
    const lw = Math.max(2, Math.min(layer.w, layer.h) * 0.16);
    ctx.save();
    ctx.strokeStyle = layer.color;
    ctx.lineWidth = lw;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(layer.x + lw / 2, layer.y + lw / 2);
    ctx.lineTo(layer.x + layer.w - lw / 2, layer.y + layer.h / 2);
    ctx.lineTo(layer.x + lw / 2, layer.y + layer.h - lw / 2);
    ctx.stroke();
    ctx.restore();
    return;
  }
  if (layer.shape === 'square') {
    const s = Math.min(layer.w, layer.h);
    ctx.fillRect(layer.x, layer.y, s, s);
  } else ctx.fillRect(layer.x, layer.y, layer.w, layer.h);
}

function drawText(ctx: CanvasRenderingContext2D, layout: Layout, env: RenderEnv, layer: TextLayer, warn: (w: Omit<Warning, 'artboard'>) => void) {
  const f = textFont(layout, env.brand, layer, env.fontFallback);
  if (!familyAvailable(f.family)) warn({ layer: layer.id, kind: 'missing_font', message: `font "${f.family}" is not available: it is not one of the bundled fonts`, blocking: true });
  ctx.save();
  ctx.font = f.css;
  ctx.fillStyle = layer.color;
  ctx.textBaseline = 'alphabetic';
  const text = f.uppercase ? layer.text.toUpperCase() : layer.text;
  const { lines, wordTooWide } = wrap(ctx, text, layer.w);
  const total = lines.length * f.lineHeight;
  if (text.trim() && (total > layer.h + 0.5 || wordTooWide)) {
    warn({ layer: layer.id, kind: 'overflow', message: `text "${layer.text.slice(0, 40)}" does not fit its box`, blocking: false });
  }
  let top = layer.y;
  if (layer.valign === 'middle') top = layer.y + (layer.h - total) / 2;
  if (layer.valign === 'bottom') top = layer.y + layer.h - total;
  // baseline inside each line box: half-leading + ascent of ~0.8 em (stable across fonts)
  const baseline = (f.lineHeight - f.size) / 2 + f.size * 0.8;
  ctx.textAlign = layer.align;
  const x = layer.align === 'left' ? layer.x : layer.align === 'center' ? layer.x + layer.w / 2 : layer.x + layer.w;
  lines.forEach((ln, i) => ctx.fillText(ln, x, top + i * f.lineHeight + baseline));
  ctx.restore();
}

/** The picture a logo layer draws: its chosen version, else the brand default. */
export function logoFor(env: RenderEnv | null, layer: { logo_id?: string | null }): HTMLImageElement | null {
  if (!env) return null;
  return layer.logo_id ? (env.logos.get(layer.logo_id) ?? null) : env.logo;
}

function drawLogo(ctx: CanvasRenderingContext2D, layer: Layer, img: HTMLImageElement) {
  const scale = Math.min(layer.w / img.naturalWidth, layer.h / img.naturalHeight);
  const dw = img.naturalWidth * scale;
  const dh = img.naturalHeight * scale;
  ctx.drawImage(img, layer.x, layer.y + (layer.h - dh) / 2, dw, dh);
}

type Warn = (w: Omit<Warning, 'artboard'>) => void;

/** A neutral box standing for the image an item will put in this slot (template drawings only). */
function drawPlaceholder(ctx: CanvasRenderingContext2D, layer: ImageLayer) {
  ctx.save();
  ctx.fillStyle = '#d9d7d0';
  ctx.fillRect(layer.x, layer.y, layer.w, layer.h);
  ctx.strokeStyle = '#b4b1a8';
  ctx.lineWidth = Math.max(2, Math.min(layer.w, layer.h) * 0.004);
  ctx.beginPath();
  ctx.moveTo(layer.x, layer.y);
  ctx.lineTo(layer.x + layer.w, layer.y + layer.h);
  ctx.moveTo(layer.x + layer.w, layer.y);
  ctx.lineTo(layer.x, layer.y + layer.h);
  ctx.stroke();
  const size = Math.max(14, Math.min(layer.w, layer.h) * 0.07);
  ctx.font = `600 ${size}px Inter, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const label = layer.slot != null ? `Image ${layer.slot + 1}` : 'Image';
  const w = ctx.measureText(label).width;
  const cx = layer.x + layer.w / 2;
  const cy = layer.y + layer.h / 2;
  ctx.fillStyle = '#d9d7d0';
  ctx.fillRect(cx - w / 2 - size * 0.4, cy - size * 0.8, w + size * 0.8, size * 1.6);
  ctx.fillStyle = '#5e5b54';
  ctx.fillText(label, cx, cy);
  ctx.restore();
}

/** Draws one layer (visible or not is the caller's business). */
function drawLayer(ctx: CanvasRenderingContext2D, layout: Layout, env: RenderEnv, layer: Layer, warn: Warn): void {
  if (layer.type === 'image') {
    const img = layer.asset_id ? env.images.get(layer.asset_id) : undefined;
    if ((!layer.asset_id || !img) && env.placeholders) {
      drawPlaceholder(ctx, layer);
      return;
    }
    if (!layer.asset_id || !img) {
      warn({ layer: layer.id, kind: 'missing_image', message: `${layer.name || 'image'}: no image chosen or it could not be loaded`, blocking: true });
      return;
    }
    const usage = env.usage.get(layer.asset_id);
    if (usage && usage !== 'approved') warn({ layer: layer.id, kind: 'asset_usage', message: `image not approved for use (${usage.replace('_', ' ')})`, blocking: true });
    const scale = drawImage(ctx, layer, img);
    if (scale > 1.05) {
      warn({ layer: layer.id, kind: 'low_resolution', message: `${layer.name || 'image'} is enlarged ×${scale.toFixed(2)}: the source is too small for this box`, blocking: false });
    }
  } else if (layer.type === 'shape') drawShape(ctx, layer);
  else if (layer.type === 'text') drawText(ctx, layout, env, layer, warn);
  else if (layer.type === 'logo') {
    const logo = logoFor(env, layer);
    if (!logo) {
      warn({ layer: layer.id, kind: 'missing_logo', message: 'logo box, but the brand kit has no such logo (or it could not be loaded)', blocking: true });
      return;
    }
    drawLogo(ctx, layer, logo);
  }
}

const noWarn: Warn = () => {};

/** True when a layer that continues on the other slides lies outside the whole strip of slides. */
function outsideStrip(layout: Layout, index: number, layer: Layer): boolean {
  const n = layout.artboards.length;
  return layer.x >= (n - index) * layout.width || layer.x + layer.w <= -index * layout.width || layer.y >= layout.height || layer.y + layer.h <= 0;
}

/** Draws one artboard in artboard pixels (the caller sets any scale) and returns its warnings.
 *  Slides sit side by side: a `span` layer of another slide is drawn here too, shifted by whole slide
 *  widths and clipped to this artboard, below this slide's own layers. */
export function renderArtboard(ctx: CanvasRenderingContext2D, layout: Layout, ab: Artboard, index: number, env: RenderEnv): Warning[] {
  const warnings: Warning[] = [];
  const warn: Warn = (w) => warnings.push({ artboard: index, ...w });
  ctx.save();
  ctx.fillStyle = ab.background;
  ctx.fillRect(0, 0, layout.width, layout.height);
  layout.artboards.forEach((other, j) => {
    if (j === index) return;
    for (const layer of other.layers) {
      if (!layer.span || layer.hidden) continue;
      const dx = (j - index) * layout.width;
      if (layer.x + dx >= layout.width || layer.x + layer.w + dx <= 0) continue;
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, layout.width, layout.height);
      ctx.clip();
      ctx.translate(dx, 0);
      drawLayer(ctx, layout, env, layer, noWarn); // its own slide reports its warnings
      ctx.restore();
    }
  });
  for (const layer of ab.layers) {
    if (layer.hidden) continue;
    const outside = layer.span ? outsideStrip(layout, index, layer) : layer.x >= layout.width || layer.y >= layout.height || layer.x + layer.w <= 0 || layer.y + layer.h <= 0;
    if (outside) warn({ layer: layer.id, kind: 'outside', message: `${layer.name || layer.type} is outside the artboard`, blocking: false });
    if (layer.span) {
      // the part on this slide is clipped to it, as the other slides' parts are
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, layout.width, layout.height);
      ctx.clip();
      drawLayer(ctx, layout, env, layer, warn);
      ctx.restore();
    } else drawLayer(ctx, layout, env, layer, warn);
  }
  ctx.restore();
  return warnings;
}

/** Full-size render of one artboard on a fresh canvas (export and QA). */
export function renderToCanvas(layout: Layout, index: number, env: RenderEnv): { canvas: HTMLCanvasElement; warnings: Warning[] } {
  const canvas = document.createElement('canvas');
  canvas.width = layout.width;
  canvas.height = layout.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2D is not available in this browser');
  const warnings = renderArtboard(ctx, layout, layout.artboards[index], index, env);
  return { canvas, warnings };
}

export function canvasBlob(canvas: HTMLCanvasElement, format: 'png' | 'jpeg'): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('the browser could not encode the image'))), format === 'png' ? 'image/png' : 'image/jpeg', 0.92),
  );
}

export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`could not load ${url}`));
    img.src = url;
  });
}
